/**
 * Helper functions for onboarding checks
 */

// Email addresses that are exempt from onboarding requirements
const EXEMPT_EMAILS = [
  'alexander.nolf47@gmail.com'
];

export const AGENCY_LEGAL_FORMS = {
  'belgië': [
    'BV (Besloten Vennootschap)',
    'NV (Naamloze Vennootschap)',
    'VOF',
    'CommV',
    'Eenmanszaak',
    'CV',
    'Andere'
  ],
  'nederland': [
    'BV',
    'NV',
    'VOF',
    'Eenmanszaak',
    'Stichting',
    'Maatschap',
    'CV',
    'Andere'
  ]
};

function normalizeCountry(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'belgie' || raw === 'belgië' || raw === 'be' || raw === 'belgium') return 'belgië';
  if (raw === 'nl' || raw === 'nederland' || raw === 'netherlands') return 'nederland';
  return raw;
}

export const getAgencyLegalFormOptions = (country) => {
  const normalized = normalizeCountry(country);
  return AGENCY_LEGAL_FORMS[normalized] || AGENCY_LEGAL_FORMS['belgië'];
};

/**
 * Check if a user email is exempt from onboarding
 * @param {string} email - User email address
 * @returns {boolean} - True if exempt, false otherwise
 */
export const isExemptFromOnboarding = (email) => {
  if (!email) return false;
  return EXEMPT_EMAILS.includes(email.toLowerCase().trim());
};

/**
 * Check if onboarding is required for a user
 * @param {Object} userData - User data from Firestore
 * @param {string} userEmail - User email address
 * @returns {boolean} - True if onboarding is required, false otherwise
 */
export const isOnboardingRequired = (userData, userEmail) => {
  // If user is exempt, never require onboarding
  if (isExemptFromOnboarding(userEmail)) {
    return false;
  }

  const role = String(userData?.role || userData?.userType || '').toLowerCase();
  const landlordType = String(userData?.landlordType || userData?.accountType || '').toLowerCase();

  // Only enforce this lightweight onboarding for private landlords.
  if (role !== 'landlord' || landlordType === 'agency') {
    return false;
  }

  const fullName =
    String(userData?.contactName || userData?.displayName || userData?.name || '').trim();
  const phone = String(userData?.phone || userData?.phoneNumber || '').trim();
  const address = String(userData?.address || userData?.adres || '').trim();
  const postalCode = String(userData?.postalCode || userData?.postcode || '').trim();
  const city = String(userData?.city || userData?.stad || '').trim();
  const country = String(userData?.country || userData?.land || '').trim();

  const hasRequiredProfile = Boolean(fullName && phone && address && postalCode && city && country);
  return !userData?.onboardingCompleted || !hasRequiredProfile;
};

export const getMissingAgencyProfileFields = (userData = {}, userEmail = '') => {
  const profile = userData?.agencyProfile || {};
  const country = normalizeCountry(profile.country || userData?.country || userData?.land || 'belgië');
  const vatApplicable = profile.vatApplicable !== false;

  const checks = [
    { key: 'companyName', label: 'Bedrijfsnaam', value: profile.companyName },
    { key: 'legalForm', label: 'Rechtsvorm', value: profile.legalForm },
    { key: 'registrationNumber', label: 'Ondernemingsnummer / KvK-nummer', value: profile.registrationNumber },
    { key: 'registeredOffice', label: 'Maatschappelijke zetel', value: profile.registeredOffice },
    { key: 'email', label: 'E-mailadres', value: profile.email || userData?.email || userEmail },
    { key: 'phone', label: 'Telefoonnummer', value: profile.phone || userData?.phone || userData?.phoneNumber },
    { key: 'contactPersonName', label: 'Naam verantwoordelijke contactpersoon', value: profile.contactPersonName || userData?.contactName },
    { key: 'country', label: 'Land', value: country }
  ];

  if (vatApplicable) {
    checks.push({
      key: 'vatNumber',
      label: 'BTW-nummer',
      value: profile.vatNumber
    });
  }

  const legalFormOptions = getAgencyLegalFormOptions(country);
  const legalForm = String(profile.legalForm || '').trim();
  const missing = checks
    .filter((item) => !String(item.value || '').trim())
    .map((item) => item.label);

  if (legalForm && !legalFormOptions.includes(legalForm)) {
    missing.push('Rechtsvorm');
  }

  return Array.from(new Set(missing));
};

export const isAgencyOnboardingRequired = (userData, userEmail) => {
  if (isExemptFromOnboarding(userEmail)) return false;
  const role = String(userData?.role || userData?.userType || '').toLowerCase();
  const landlordType = String(userData?.landlordType || userData?.accountType || '').toLowerCase();
  if (role !== 'landlord' || landlordType !== 'agency') return false;
  return getMissingAgencyProfileFields(userData, userEmail).length > 0;
};
